import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Tag,
  Folder,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';
import { DeletionProtectionModal } from '../../components/UI';
import { useDeletionProtection } from '../../hooks/useDeletionProtection';

interface ProductCategory {
  _id: string;
  name: string;
  description?: string;
  parentCategory?: {
    _id: string;
    name: string;
  };
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

const ProductCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [parentCategories, setParentCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    parentCategory: '',
    status: 'active' as 'active' | 'inactive',
  });

  const { settings } = useSettings();

  // Deletion protection
  const {
    showDeletionProtectionModal,
    deletionProtectionData,
    checkAndDelete,
    closeDeletionProtectionModal
  } = useDeletionProtection();

  useEffect(() => {
    fetchCategories();
    fetchParentCategories();
  }, []);

  // Soft refresh: refetch categories when header clears cache
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateMasterDataCache();
      fetchCategories();
      fetchParentCategories();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/categories?limit=1000'); // Request all categories
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchParentCategories = async () => {
    try {
      const response = await apiService.get('/categories/all');
      setParentCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching parent categories:', error);
    }
  };

  const handleAddCategory = async () => {
    try {
      const categoryData = {
        ...newCategory,
        parentCategory: newCategory.parentCategory || undefined
      };
      const response = await apiService.post('/categories', categoryData);
      toast.success('Category added successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowAddModal(false);
      setNewCategory({
        name: '',
        description: '',
        parentCategory: '',
        status: 'active',
      });
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCategories();
        await fetchParentCategories();
      }, 100);
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory) return;
    
    try {
      const categoryData = {
        ...editingCategory,
        parentCategory: editingCategory.parentCategory?._id || undefined
      };
      const response = await apiService.put(`/categories/${editingCategory._id}`, categoryData);
      toast.success('Category updated successfully');
      
      // Immediately clear master data cache and force refresh
      apiService.invalidateMasterDataCache();
      
      setShowEditModal(false);
      setEditingCategory(null);
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchCategories();
        await fetchParentCategories();
      }, 100);
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const category = categories.find(c => c._id === categoryId);
    if (!category) return;

    const deleteFunction = async () => {
      try {
        const response = await apiService.delete(`/categories/${categoryId}`);
        toast.success('Category deleted successfully');
        
        // Immediately clear master data cache and force refresh
        apiService.invalidateMasterDataCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchCategories();
          await fetchParentCategories();
        }, 100);
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      }
    };

    await checkAndDelete('categories', categoryId, category.name, deleteFunction);
  };

  const openEditModal = (category: ProductCategory) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.parentCategory?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || category.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const exportCategories = async () => {
    try {
      await apiService.download('/categories/export/excel', 'product-categories.xlsx');
      toast.success('Categories exported successfully');
    } catch (error) {
      console.error('Error exporting categories:', error);
      toast.error('Failed to export categories');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>Product Categories</h1>
          <p style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>
            Manage your product categories and classifications
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateMasterDataCache(); 
              fetchCategories(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            onClick={() => setShowAddModal(true)}
            icon={<Plus className="w-5 h-5" />}
          >
            Add Category
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm p-4" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
          </div>
          <button
            onClick={exportCategories}
            className="flex items-center space-x-2 px-4 py-2 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 pt-4 border-t border-[var(--color-border)]"
          >
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-surface)] text-[var(--color-text)]"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Categories Table */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-sm overflow-hidden" style={{ borderRadius: 'var(--border-radius)' } as any}>
        <DoubleScrollbar>
          <table className="min-w-full divide-y divide-[var(--color-border)]">
            <thead className="bg-[var(--color-surface-light)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Parent Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider font-['Times_New_Roman']">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--color-background)] divide-y divide-[var(--color-border)]">
              {filteredCategories.map((category) => (
                <tr key={category._id} className="hover:bg-[var(--color-surface-light)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Tag className="w-5 h-5 text-[var(--color-primary)] mr-2" />
                      <div className="text-sm font-medium text-[var(--color-text)] font-['Times_New_Roman']">
                        {category.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {category.parentCategory ? (
                        <>
                          <Folder className="w-4 h-4 text-[var(--color-success)] mr-2" />
                          <span className="text-sm text-[var(--color-text)] font-['Times_New_Roman']">
                            {category.parentCategory.name}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
                          Root Category
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-[var(--color-text)] font-['Times_New_Roman']">
                      {category.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full font-['Times_New_Roman'] ${
                      category.status === 'active' 
                        ? 'bg-[var(--color-success-light)] text-[var(--color-success-dark)]' 
                        : 'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)]'
                    }`}>
                      {category.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{formatDateTime(category.createdAt, settings)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(category)}
                        className="text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-['Times_New_Roman']"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category._id)}
                        className="text-[var(--color-danger)] hover:text-[var(--color-danger-dark)] font-['Times_New_Roman']"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DoubleScrollbar>

        {/* Empty State */}
        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
              No categories found
            </h3>
            <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              {searchTerm || statusFilter ? 'Try adjusting your search or filters.' : 'Get started by creating your first category.'}
            </p>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Category"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={newCategory.name}
            onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
            placeholder="Enter category name"
            required
          />
          <Input
            label="Description"
            value={newCategory.description}
            onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
            placeholder="Enter category description"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Parent Category (Optional)
            </label>
            <select
              value={newCategory.parentCategory}
              onChange={e => setNewCategory({ ...newCategory, parentCategory: e.target.value })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="">No Parent (Root Category)</option>
              {parentCategories.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
              Status
            </label>
            <select
              value={newCategory.status}
              onChange={e => setNewCategory({ ...newCategory, status: e.target.value as 'active' | 'inactive' })}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowAddModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleAddCategory}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Add Category
          </button>
        </div>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Category"
      >
        {editingCategory && (
          <div className="space-y-4">
            <Input
              label="Name"
              value={editingCategory.name}
              onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
              placeholder="Enter category name"
              required
            />
            <Input
              label="Description"
              value={editingCategory.description || ''}
              onChange={e => setEditingCategory({ ...editingCategory, description: e.target.value })}
              placeholder="Enter category description"
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Parent Category (Optional)
              </label>
              <select
                value={editingCategory.parentCategory?._id || ''}
                onChange={e => setEditingCategory({ 
                  ...editingCategory, 
                  parentCategory: e.target.value ? { _id: e.target.value, name: '' } : undefined 
                })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="">No Parent (Root Category)</option>
                {parentCategories
                  .filter(parent => parent._id !== editingCategory._id) // Prevent self-selection
                  .map((parent) => (
                    <option key={parent._id} value={parent._id}>
                      {parent.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1 font-['Times_New_Roman']">
                Status
              </label>
              <select
                value={editingCategory.status}
                onChange={e => setEditingCategory({ ...editingCategory, status: e.target.value as 'active' | 'inactive' })}
                className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman'] bg-[var(--color-background)] text-[var(--color-text)]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowEditModal(false)}
            className="px-4 py-2 text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-light)] transition-colors font-['Times_New_Roman']"
          >
            Cancel
          </button>
          <button
            onClick={handleEditCategory}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--color-on-primary)] rounded-lg hover:bg-[var(--color-primary-dark)] transition-colors font-['Times_New_Roman']"
          >
            Update Category
          </button>
        </div>
      </Modal>

      {/* Deletion Protection Modal */}
      <DeletionProtectionModal
        isOpen={showDeletionProtectionModal}
        onClose={closeDeletionProtectionModal}
        entityName={deletionProtectionData?.entityName || ''}
        entityType={deletionProtectionData?.entityType || ''}
        dependencies={deletionProtectionData?.dependencies || []}
        onViewDetails={(dependency) => {
          // Navigate to the dependency details
          console.log('View details for:', dependency);
          // You can implement navigation here if needed
        }}
      />
    </div>
  );
};

export default ProductCategoriesPage; 